import { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  ArrowRightLeft,
  Container,
  Mail,
  FileClock,
  LogOut,
  CircleUser,
  Boxes,
  Receipt,
  FileText,
  ClipboardList,
  Send,
  FileBarChart,
  Users as UsersIcon,
  Droplets,
  Wrench,
  Building2,
  KeyRound,
  BookOpen,
  Inbox,
  PackageOpen,
  Smartphone,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import DepotPicker from "./DepotPicker";
import GateEventFab from "./GateEventFab";
import PreviewEnvBanner from "./PreviewEnvBanner";
import PwaInstallBanner from "./PwaInstallBanner";

// RBAC role sets (mirror backend bundles in core.py)
const ALL_NON_OPERATOR  = ["admin", "ceo", "general_manager", "depot_manager", "finance", "supervisor"];
const RENTALS_VIEW      = ["admin", "ceo", "general_manager", "finance", "supervisor"]; // no depot_mgr, no operator

const navItems = [
  { to: "/",             label: "Dashboard",     icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/gate-events",  label: "Gate Activity",  icon: ArrowRightLeft,             testid: "nav-gate-events" },
  { to: "/gate",         label: "Gate App (Mobile)", icon: Smartphone,
    roles: ["admin", "supervisor", "depot_manager", "operator"],                    testid: "nav-gate-app" },
  { to: "/document-intake", label: "Expected Arrivals", icon: Inbox,              testid: "nav-document-intake" },
  { to: "/wash-station", label: "Wash Station",  icon: Droplets,                   testid: "nav-wash" },
  { to: "/repairs",      label: "Tank Repairs",  icon: Wrench,                     testid: "nav-repairs" },
  { to: "/rentals",      label: "Rentals",       icon: KeyRound,                   testid: "nav-rentals",
    roles: RENTALS_VIEW },
  { to: "/stock",        label: "Empty Stock",   icon: Boxes,                      testid: "nav-stock" },
  { to: "/containers",   label: "Containers in the yard",  icon: Container,                  testid: "nav-containers" },
  { to: "/partners",     label: "Customers",     icon: Building2,                  testid: "nav-partners" },
  { to: "/tariffs",      label: "Tariffs",       icon: Receipt,                    testid: "nav-tariffs",
    roles: ALL_NON_OPERATOR },
  { to: "/cargo-products", label: "Cargo Products", icon: PackageOpen,              testid: "nav-cargo-products",
    roles: ALL_NON_OPERATOR },
  { to: "/invoices",     label: "Invoices",      icon: FileText,                   testid: "nav-invoices",
    roles: ALL_NON_OPERATOR },
  { to: "/reports",      label: "Reports",       icon: FileBarChart,               testid: "nav-reports" },
  { to: "/users",        label: "Users",         icon: UsersIcon, roles: ["admin"], testid: "nav-users" },
  // ─── secondary / system ───
  { to: "/eirs",            label: "EIR Register",   icon: ClipboardList, testid: "nav-eirs",           group: "secondary" },
  { to: "/edi",             label: "EDI Messages",   icon: Mail,          testid: "nav-edi",            group: "secondary" },
  { to: "/release-orders",  label: "Release Orders", icon: PackageOpen,   testid: "nav-release-orders", group: "secondary" },
  { to: "/edi-recipients",  label: "EDI Recipients", icon: Send,          testid: "nav-edi-recipients", group: "secondary",
    roles: ["admin", "supervisor", "general_manager", "depot_manager"] },
  { to: "/audit",           label: "Audit Log",      icon: FileClock,     testid: "nav-audit",          group: "secondary",
    roles: ALL_NON_OPERATOR },
  { to: "/help",            label: "User Manual",    icon: BookOpen,      testid: "nav-help",           group: "secondary" },
];

function SidebarContent({ user, onLogout, onNavigate }) {
  return (
    <>
      <div className="px-5 py-5 border-b border-white/10 flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-white/95 flex items-center justify-center p-1.5 shadow-sm">
          <img src="/brand/portside-logo.svg" alt="Portside" className="w-full h-full object-contain" />
        </div>
        <div>
          <div className="font-display font-extrabold tracking-tight text-white leading-none text-base">Portside</div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#99D420]/90 mt-1 font-mono">MTG · Operations</div>
        </div>
      </div>
      <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between gap-2">
        <div className="text-[9px] uppercase tracking-[0.22em] text-gray-400/70 font-mono">Depot</div>
        <DepotPicker />
      </div>

      <nav className="flex-1 py-3 overflow-y-auto" data-testid="sidebar-nav">
        {(() => {
          const visible = navItems.filter((n) => !n.roles || n.roles.includes(user?.role));
          const primary = visible.filter((n) => n.group !== "secondary");
          const secondary = visible.filter((n) => n.group === "secondary");
          const renderItem = (item) => {
            const Icon = item.icon;
            const muted = item.group === "secondary";
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={onNavigate}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 transition-colors ${
                    isActive
                      ? "bg-white/10 text-white border-[#99D420] font-medium"
                      : muted
                        ? "text-gray-400/70 border-transparent hover:text-white hover:bg-white/5 text-[13px]"
                        : "text-gray-300/85 border-transparent hover:text-white hover:bg-white/5 font-medium"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            );
          };
          return (
            <>
              {primary.map(renderItem)}
              {secondary.length > 0 && (
                <>
                  <div className="px-5 pt-5 pb-2 text-[9px] uppercase tracking-[0.25em] text-gray-500/70 font-mono">
                    System
                  </div>
                  {secondary.map(renderItem)}
                </>
              )}
            </>
          );
        })()}
      </nav>

      <div className="px-5 py-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-[#008DC6]/30 flex items-center justify-center">
            <CircleUser className="w-4 h-4 text-[#99D420]" />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-semibold text-white truncate" data-testid="current-user-name">
              {user?.name}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#99D420]/80 font-mono" data-testid="current-user-role">
              {user?.role}
            </div>
          </div>
        </div>
        <button
          onClick={onLogout}
          data-testid="logout-button"
          className="w-full inline-flex items-center justify-center gap-2 h-8 px-3 text-xs font-semibold rounded-md border border-white/20 text-gray-200 hover:bg-white/10 hover:border-[#99D420]/60 transition-colors"
        >
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </div>
    </>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#FFFBEB] text-[#1A1410] md:flex" data-testid="app-shell">
      {/* Mobile top bar (visible <md only) */}
      <header
        className="md:hidden sticky top-0 z-40 flex items-center gap-3 border-b border-[#003147]/40 bg-[#003147] text-white px-4 py-3 ps-mobile-topbar"
        data-testid="mobile-topbar"
      >
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="p-1.5 -ml-1 rounded-md hover:bg-white/10 active:bg-white/20 transition-colors"
          aria-label="Open menu"
          data-testid="mobile-menu-open"
        >
          <Menu className="w-6 h-6" />
        </button>
        <div className="w-8 h-8 rounded-md bg-white/95 flex items-center justify-center p-1 shadow-sm shrink-0">
          <img src="/brand/portside-logo.svg" alt="Portside" className="w-full h-full object-contain" />
        </div>
        <div className="min-w-0">
          <div className="font-display font-extrabold tracking-tight leading-none text-sm">Portside</div>
          <div className="text-[9px] uppercase tracking-[0.22em] text-[#99D420]/90 mt-0.5 font-mono">MTG · Operations</div>
        </div>
        <div className="ml-auto">
          <DepotPicker />
        </div>
      </header>

      {/* Desktop sidebar (visible md+ only) */}
      <aside
        className="hidden md:flex w-60 shrink-0 border-r border-[#003147]/40 bg-[#003147] text-gray-200 flex-col"
        data-testid="sidebar-desktop"
      >
        <SidebarContent user={user} onLogout={handleLogout} />
      </aside>

      {/* Mobile drawer (visible <md when open) */}
      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-50 bg-black/50"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
            data-testid="mobile-drawer-overlay"
          />
          <aside
            className="md:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-[#003147] text-gray-200 flex flex-col shadow-2xl animate-slide-in-left"
            data-testid="mobile-drawer"
          >
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="absolute right-3 p-1.5 rounded-md text-gray-300 hover:text-white hover:bg-white/10 ps-drawer-close"
              aria-label="Close menu"
              data-testid="mobile-menu-close"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarContent user={user} onLogout={handleLogout} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0 overflow-x-hidden bg-[#FFFBEB]">
        <PreviewEnvBanner />
        <Outlet />
      </main>
      <GateEventFab />
      <PwaInstallBanner />
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, testid }) {
  return (
    <div
      className="border-b border-[#E5E0D2] bg-white px-4 sm:px-6 md:px-8 py-4 sm:py-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4 relative"
      data-testid={testid || "page-header"}
    >
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#006E9E] via-[#99D420] to-[#F1471D]" />
      <div className="pl-3 min-w-0">
        <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-black tracking-tight text-[#1A1410] leading-tight break-words">
          {title}
        </h1>
        {subtitle && <p className="text-xs sm:text-sm text-[#5A4E42] mt-1.5 max-w-2xl leading-relaxed">{subtitle}</p>}
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-2 pl-3 sm:pl-0">
          {actions}
        </div>
      )}
    </div>
  );
}

export { Link };
